import { i18n, isEn } from '../../i18n';
import { CustomException } from '@tencent/ppd-base-libs';
import { batchGetCoupons } from './api';
import { CouponState, CouponInfo, DateType, CardType } from '../../type';

/**
 * 券状态文案映射
 */
const COUPON_STATE_TEXT = {
  [CouponState.Normal]: i18n('領取'),
  [CouponState.Got]: i18n('已領取'),
  [CouponState.Empty]: i18n('已領完'),
  [CouponState.Used]: i18n('已使用'),
  [CouponState.Expired]: i18n('已過期'),
};

const COUPON_BTN_TEXT = {
  [CouponState.Normal]: i18n('領取'),
  [CouponState.Got]: i18n('去使用'), // 已领取的按钮展示为去使用
  [CouponState.Empty]: i18n('領取'),  // 要注意英文文案是否超长
  [CouponState.Used]: i18n('已使用'),
  [CouponState.Expired]: i18n('已過期'),
};

/**
 * 获取状态对应文案
 * @param state 状态枚举
 * @returns
 */
export function getCouponStateText(state: CouponState) {
  return COUPON_STATE_TEXT[state] || COUPON_STATE_TEXT[CouponState.Empty];
}

/**
 * 获取按钮对应文案
 * @param state 状态枚举
 * @returns
 */
export function getCouponBtnText(state: CouponState) {
  return COUPON_BTN_TEXT[state] || '';
}

/**
 * 获取时间展示字符串
 * @param dateTime 时间信息
 */
export function getDateString(dateTime?: string) {
  if (dateTime) {
    return dateTime.split(' ')[0].replace(/-/g, '.');
  }
  return '';
}

/**
 * 获取可用时间
 */
export function getUseDate(couponInfo: CouponInfo) {
  const { validType, validTime, expireType, expireTime, expireDay } = couponInfo;
  if (validType === DateType.absolute && expireType === DateType.absolute) {
    return i18n('{{startTime}}-{{endTime}}有效', {
      startTime: getDateString(validTime),
      endTime: getDateString(expireTime),
    });
  }
  if (validType === DateType.relative && expireType === DateType.relative) {
    return i18n('領取後{{expireDay}}天內可使用', { expireDay });
  }
  return '';
}

/**
 * 获领取提示文案
 * @param state 状态枚举
 * @returns
 */
export function getCouponTips(couponInfo: CouponInfo) {
  const { state, startTime } = couponInfo;
  switch (state) {
    case CouponState.Empty:
      return i18n('券已兌罄');
    case CouponState.Locked:
      return i18n('{{date}}後可領取', { date: getDateString(startTime) });
    default:
      return '';
  }
}

/**
 * 计算金额信息
 * 对于小于1港币的金额，认为是折扣价，如：9.5折
 * 对于大于等于1的金额，则为券的实际优惠金额
 * @param
 * @returns
 */
export function getCouponValue(couponValue: string) {
  const value = parseFloat(couponValue);
  const result = {
    isOff: false,
    value: `${value}`,
  };
  if (value < 1 && value > 0) {
    const val = isEn() ? (100 - value * 100) : value * 10;
    Object.assign(result, {
      value: val.toFixed(2).replace(/(\.)?0+$/, ''),
      isOff: true,
    });
  }
  return result;
}

/**
 * 领券
 * @param spid 券所属商户
 * @param couponId 券ID
 * @returns
 */
export async function getCoupon(spid: string, couponId: string) {
  const res = await getBatchCoupons(spid, couponId);
  if (!res.isSuccess) {
    throw new CustomException(res, 'getCouponException', `get coupon ${couponId} fail`);
  }
  return res.result[0];
}

/**
 * 批量领取商户展示券
 * @param spid 商户ID
 * @param couponId 领取指定ID的单张消费券，只有单张时才需传入
 * @returns
 */
export async function getBatchCoupons(spid: string, couponId = '') {
  const res = await batchGetCoupons(spid, couponId);
  // 总个数
  const totalCount = res.length;
  const successRes = res.filter(item => item.recvState === 0);
  // 领取成功个数
  const successCount = successRes.length;
  return {
    /** 是否全部领取成功 */
    isSuccess: successCount > 0,
    /** 券领取成功个数 */
    successCount,
    /** 领券总数 */
    totalCount,
    /** 是否需要刷新券 */
    needRefresh: res.filter(item => item.recvState === 3).length > 0,
    /** 领取成功的结果 */
    result: successRes,
  };
}

/**
 * 券信息详情列表
 */
export function getDetailInfo(couponInfo: CouponInfo | null): {
  label: string;
  info: string;
}[] {
  if (!couponInfo) return [];
  if (couponInfo.type === CardType.AutoCoupon) {
    // 优惠券(自动抵扣券)
    return [
      {
        label: i18n('使用時間：'),
        info: getUseDate(couponInfo!),
      },
      {
        label: i18n('使用門檻：'),
        info: `${couponInfo.couponRange}${isEn() ? ' ' : ''}${i18n('消費滿HK${{X}}時可用', { X: couponInfo.requireNum })}`,
      },
    ];
  }
  // 展示券
  return [
    {
      label: i18n('可用商戶：'),
      info: couponInfo.couponRange,
    },
    {
      label: i18n('使用時間：'),
      info: getUseDate(couponInfo!),
    },
    {
      label: i18n('使用門檻：'),
      info: couponInfo.couponCondition,
    },
  ];
}
